import React, { useState } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';

export const ElectricityBillModal = ({ onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    bill_reference: '', supplier: 'Ergon Energy',
    period_start: '', period_end: '',
    total_amount: '', government_rebate: '75.00',
    usage_kwh: '', notes: ''
  });

  const netAmount = (parseFloat(form.total_amount || 0) - parseFloat(form.government_rebate || 0)).toFixed(2);

  const handleSubmit = async () => {
    if (!form.bill_reference || !form.period_start || !form.period_end || !form.total_amount) {
      showToast('Please fill in all required fields', 'error'); return;
    }
    setSaving(true);
    try {
      await apiClient.post('/pet400/electricity-bills', {
        ...form,
        total_amount: parseFloat(form.total_amount),
        government_rebate: parseFloat(form.government_rebate || 0),
        usage_kwh: form.usage_kwh ? parseFloat(form.usage_kwh) : null
      });
      showToast('✅ Electricity bill loaded — sessions updated!');
      onSuccess?.(); onClose();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save bill', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-5 py-4 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-white font-bold">⚡ Load Electricity Bill</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Bill Reference *</label>
              <input value={form.bill_reference} onChange={e => setForm(f => ({...f, bill_reference: e.target.value}))}
                placeholder="e.g. A-2373A5D0-006"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Period Start *</label>
              <input type="date" value={form.period_start} onChange={e => setForm(f => ({...f, period_start: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Period End *</label>
              <input type="date" value={form.period_end} onChange={e => setForm(f => ({...f, period_end: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Total Amount ($) *</label>
              <input type="number" step="0.01" value={form.total_amount}
                onChange={e => setForm(f => ({...f, total_amount: e.target.value}))}
                placeholder="e.g. 2368.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Govt Rebate ($)</label>
              <input type="number" step="0.01" value={form.government_rebate}
                onChange={e => setForm(f => ({...f, government_rebate: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Usage (kWh)</label>
              <input type="number" step="1" value={form.usage_kwh}
                onChange={e => setForm(f => ({...f, usage_kwh: e.target.value}))}
                placeholder="Optional"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-400" />
            </div>
          </div>
          {form.total_amount && parseFloat(form.total_amount) > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
              <p className="text-yellow-800 font-medium">
                Net amount after rebate: <span className="text-yellow-900 font-bold">${netAmount}</span>
              </p>
              <p className="text-yellow-600 text-xs mt-1">Informational only — this amount will be allocated across all sessions in this billing period.</p>
            </div>
          )}
          <button onClick={handleSubmit} disabled={saving}
            className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl text-sm">
            {saving ? '⏳ Saving...' : '⚡ Load Bill'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const MaintenanceLogModal = ({ onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    log_type: 'effluent',
    notes: ''
  });

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await apiClient.post('/pet400/maintenance-logs', form);
      showToast(`✅ ${form.log_type === 'effluent' ? 'Effluent collection' : 'Machine clean'} recorded!`);
      onSuccess?.(); onClose();
    } catch (err) {
      showToast('Failed to save', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className={`px-5 py-4 rounded-t-2xl flex items-center justify-between ${form.log_type === 'effluent' ? 'bg-gradient-to-r from-teal-500 to-cyan-600' : 'bg-gradient-to-r from-purple-500 to-indigo-600'}`}>
          <h2 className="text-white font-bold">
            {form.log_type === 'effluent' ? '🌊 Effluent Collection' : '🧹 Machine Clean'}
          </h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setForm(f => ({...f, log_type: 'effluent'}))}
              className={`p-3 rounded-lg border-2 text-sm font-medium ${form.log_type === 'effluent' ? 'bg-teal-50 border-teal-500 text-teal-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
              🌊 Effluent
            </button>
            <button onClick={() => setForm(f => ({...f, log_type: 'clean'}))}
              className={`p-3 rounded-lg border-2 text-sm font-medium ${form.log_type === 'clean' ? 'bg-purple-50 border-purple-500 text-purple-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
              🧹 Machine Clean
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
            <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
              rows={3} placeholder="Volume, service company, observations..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 resize-none" />
          </div>
          <button onClick={handleSubmit} disabled={saving}
            className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl text-sm">
            {saving ? '⏳ Saving...' : '✅ Record Log'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// ADD COST TO EXISTING EFFLUENT COLLECTION
// ============================================================
export const AddEffluentCostModal = ({ log, onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    cost: '',
    supplier: log?.supplier || '',
    litres:   log?.litres   || '',
    notes:    log?.notes    || ''
  });

  const handleSubmit = async () => {
    if (!form.cost) { showToast('Please enter the service fee', 'error'); return; }
    setSaving(true);
    try {
      const res = await apiClient.put(`/pet400/maintenance-logs/${log.id}/add-cost`, {
        cost:     parseFloat(form.cost),
        supplier: form.supplier || undefined,
        litres:   form.litres ? parseFloat(form.litres) : undefined,
        notes:    form.notes  || undefined
      });
      showToast(`✅ ${res.data.message}`);
      onSuccess?.();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save cost', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="bg-gradient-to-r from-teal-600 to-cyan-700 px-5 py-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold">💰 Add Effluent Fee</h2>
            <p className="text-teal-200 text-xs">Collection on {log?.date}</p>
          </div>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-800">
            💡 Enter the service fee from the invoice. The cost will be automatically allocated across sessions since the previous collection.
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Service Fee ($) *</label>
            <div className="flex items-center gap-1">
              <span className="text-gray-400">$</span>
              <input type="number" step="0.01" value={form.cost}
                onChange={e => setForm(f => ({...f, cost: e.target.value}))}
                placeholder="e.g. 2097.90" autoFocus
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Supplier</label>
            <select value={form.supplier} onChange={e => setForm(f => ({...f, supplier: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
              <option value="">Select supplier...</option>
              <option>JJ's Waste & Recycling</option>
              <option>Tropical Waste Services</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Volume (litres)</label>
            <input type="number" value={form.litres}
              onChange={e => setForm(f => ({...f, litres: e.target.value}))}
              placeholder="e.g. 8500"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>

          <button onClick={handleSubmit} disabled={saving}
            className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl text-sm">
            {saving ? '⏳ Saving...' : '✅ Add Cost & Allocate'}
          </button>
        </div>
      </div>
    </div>
  );
};



export const WaterBillModal = ({ onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    bill_reference: '', supplier: 'Colliers / Water Authority',
    period_start: '', period_end: '',
    total_amount: '', gst_amount: '', notes: ''
  });

  const netAmount = form.total_amount && parseFloat(form.total_amount) > 0
    ? (parseFloat(form.total_amount || 0) - parseFloat(form.gst_amount || 0)).toFixed(2)
    : null;

  const handleSubmit = async () => {
    if (!form.bill_reference || !form.period_start || !form.period_end || !form.total_amount) {
      showToast('Please fill in all required fields', 'error'); return;
    }
    setSaving(true);
    try {
      await apiClient.post('/pet400/water-bills', {
        ...form,
        total_amount: parseFloat(form.total_amount),
        gst_amount: parseFloat(form.gst_amount || 0),
      });
      showToast('✅ Water bill loaded — sessions updated!');
      onSuccess?.(); onClose();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save bill', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-5 py-4 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-white font-bold">🚿 Load Water Bill</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Invoice / Tax Inv. No. *</label>
              <input value={form.bill_reference} onChange={e => setForm(f => ({...f, bill_reference: e.target.value}))}
                placeholder="e.g. 41574"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Supplier</label>
              <input value={form.supplier} onChange={e => setForm(f => ({...f, supplier: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Period Start *</label>
              <input type="date" value={form.period_start} onChange={e => setForm(f => ({...f, period_start: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Period End *</label>
              <input type="date" value={form.period_end} onChange={e => setForm(f => ({...f, period_end: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Total incl. GST ($) *</label>
              <input type="number" step="0.01" value={form.total_amount}
                onChange={e => setForm(f => ({...f, total_amount: e.target.value}))}
                placeholder="e.g. 92.08"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">GST Amount ($)</label>
              <input type="number" step="0.01" value={form.gst_amount}
                onChange={e => setForm(f => ({...f, gst_amount: e.target.value}))}
                placeholder="e.g. 8.37"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>
          {netAmount && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              Net excl. GST: <strong>${netAmount}</strong> — will be split across all sessions in this billing period
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
            <input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
              placeholder="e.g. Chamber 3, 1-19 Cormack Rd, Wingfield"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
          </div>
          <button onClick={handleSubmit} disabled={saving}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl text-sm">
            {saving ? '⏳ Saving...' : '🚿 Load Bill & Allocate to Sessions'}
          </button>
        </div>
      </div>
    </div>
  );
};
